import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Input, Select, Descriptions } from "antd";
import Icon from "@mui/material/Icon";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import DataTable from "pages/Tables/DataTable";
import { useToast } from "components/Toast";

import { getNonAdminUsers, adminSetUserPermission } from "api";

const { Option } = Select;
const { TextArea } = Input;

const UserManager = () => {
  const toast = useToast();

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [selectedLoginStatus, setSelectedLoginStatus] = useState("all");
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    allow_login: "",
  });

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // 权限设置弹窗
  const [permissionVisible, setPermissionVisible] = useState(false);
  const [editForm, setEditForm] = useState({
    account_id: "",
    allow_login: 1,
    is_minsu_admin: 1,
    reason: "",
  });

  // 打开权限设置
  const handleOpenPermission = (item) => {
    setCurrentItem(item);
    setEditForm({
      account_id: item.account_id,
      allow_login: item.allow_login || 1,
      is_minsu_admin: item.minsu_admin || 1,
      reason: item.reason || "",
    });
    setPermissionVisible(true);
  };

  // 关闭权限设置
  const handleClosePermission = () => {
    setPermissionVisible(false);
  };

  // 打开详情
  const handleRowClick = (index) => {
    setCurrentItem(userData[index]);
    setDetailVisible(true);
  };

  // 关闭详情
  const handleCloseDetail = () => {
    setDetailVisible(false);
    setCurrentItem(null);
  };

  // 获取用户列表
  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getNonAdminUsers({
        page: currentPage + 1,
        page_size: 10,
        keyword: searchParams.keyword,
        allow_login: searchParams.allow_login,
      });

      if (res.status === 0) {
        setUserData(res.result.list || []);
        setTotal(res.result.total || 0);
      } else {
        toast(`加载失败：${res.msg}`, "error");
      }
    } catch (err) {
      console.error(err);
      toast("网络异常", "error");
    } finally {
      setLoading(false);
    }
  }, [searchParams, currentPage, toast]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // 搜索
  const handleSearch = () => {
    setSearchParams({
      keyword: searchText,
      allow_login: selectedLoginStatus === "all" ? "" : selectedLoginStatus,
    });
    setCurrentPage(0);
  };

  // 重置
  const resetFilters = () => {
    setSearchText("");
    setSelectedLoginStatus("all");
    setSearchParams({ keyword: "", status: "" });
    setCurrentPage(0);
  };

  // 分页
  const handlePageChange = (newPageIndex) => {
    setCurrentPage(newPageIndex);
  };

  // 保存权限
  const handleSavePermission = async () => {
    try {
      setLoading(true);
      const res = await adminSetUserPermission(editForm);
      if (res.status !== 0) throw new Error(res.msg);
      toast("权限设置成功", "success");
      handleClosePermission();
      fetchUserData();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // 状态标签
  const LoginBadge = ({ allow_login }) => {
    if (allow_login === 1)
      return <MDBadge badgeContent="允许登录" color="success" size="sm" />;
    return <MDBadge badgeContent="禁止登录" color="error" size="sm" />;
  };

  const MinsuBadge = ({ minsu_admin }) => {
    if (minsu_admin === 2)
      return <MDBadge badgeContent="民宿管理员" color="success" size="sm" />;
    return <MDBadge badgeContent="普通用户" color="default" size="sm" />;
  };

  // 表格列
  const { columns, rows } = useMemo(
    () => ({
      columns: [
        { Header: "账号ID", accessor: "account_id", align: "left" },
        { Header: "昵称", accessor: "account_name", align: "left" },
        { Header: "手机号", accessor: "phone", align: "left" },
        { Header: "登录权限", accessor: "allow_login", align: "center" },
        { Header: "民宿权限", accessor: "minsu_admin", align: "center" },
        { Header: "操作", accessor: "action", align: "right" },
      ],

      rows: userData.map((item) => ({
        account_id: item.account_id,
        account_name: item.account_name || "-",
        phone: item.phone || "-",
        allow_login: <LoginBadge allow_login={item.allow_login} />,
        minsu_admin: <MinsuBadge minsu_admin={item.minsu_admin} />,
        action: (
          <MDTypography
            component="div"
            variant="caption"
            sx={{
              display: "flex",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            <MDButton
              color="info"
              size="small"
              sx={{ px: 1, minWidth: "auto" }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPermission(item);
              }}
              disabled={loading}
            >
              设置权限
            </MDButton>
          </MDTypography>
        ),
      })),
    }),
    [userData, loading]
  );

  const banCount = userData.filter((i) => i.allow_login === 2).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* 顶部统计 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: "1px solid #e0e0e0",
          marginBottom: "16px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
            <Icon
              sx={{
                color: ({ palette: { error } }) => error.main,
                marginRight: "8px",
              }}
            >
              block
            </Icon>
            <MDTypography variant="body2" color="textSecondary">
              <strong>{banCount} 人禁止登录</strong> | 共 {total} 个用户
            </MDTypography>
          </div>
        </div>
      </div>

      {/* 搜索栏 */}
      <div
        style={{
          padding: "16px 24px",
          marginBottom: "24px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <MDTypography variant="body2" style={{ marginRight: 12 }}>
            登录状态：
          </MDTypography>
          <Select
            value={selectedLoginStatus}
            onChange={setSelectedLoginStatus}
            style={{ width: 160 }}
            size="small"
          >
            <Option value="all">全部</Option>
            <Option value={1}>允许登录</Option>
            <Option value={2}>禁止登录</Option>
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", flexGrow: 1, maxWidth: 400, whiteSpace: "nowrap"  }}>
          <MDTypography variant="body2" style={{ marginRight: 12 }}>
            搜索：
          </MDTypography>
          <Input
            placeholder="账号ID / 昵称 / 手机号"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            size="small"
            style={{ flexGrow: 1 }}
          />
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <MDButton
            color="info"
            size="small"
            onClick={handleSearch}
            disabled={loading}
          >
            搜索
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={resetFilters}
          >
            重置
          </MDButton>
        </div>
      </div>

      {/* 表格 */}
      <DataTable
        table={{ columns, rows }}
        showTotalEntries
        isSorted={false}
        noEndBorder
        entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
        loading={loading}
        total={total}
        currentPage={currentPage}
        changePage={handlePageChange}
        onRowClick={handleRowClick}
      />

      {/* 详情弹窗 */}
      <Modal
        title="用户详情"
        visible={detailVisible}
        onCancel={handleCloseDetail}
        footer={null}
        width={700}
        maskClosable
        zIndex={9999}
      >
        {currentItem && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="账号ID">{currentItem.account_id}</Descriptions.Item>
            <Descriptions.Item label="昵称">{currentItem.account_name || "-"}</Descriptions.Item>
            <Descriptions.Item label="手机号">{currentItem.phone || "-"}</Descriptions.Item>
            <Descriptions.Item label="真实姓名">{currentItem.real_name || "-"}</Descriptions.Item>
            <Descriptions.Item label="是否允许登录">
              <LoginBadge allow_login={currentItem.allow_login} />
            </Descriptions.Item>
            <Descriptions.Item label="是否是民宿管理员">
              <MinsuBadge minsu_admin={currentItem.minsu_admin} />
            </Descriptions.Item>
            <Descriptions.Item label="禁用原因">
              {currentItem.reason || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {currentItem.created_at || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 权限设置弹窗 */}
      <Modal
        title="设置用户权限"
        visible={permissionVisible}
        onCancel={handleClosePermission}
        onOk={handleSavePermission}
        width={500}
        maskClosable
        zIndex={9999}
      >
        <div style={{ marginBottom: 16 }}>
          <MDTypography variant="body2" fontWeight="bold">
            账号：{currentItem?.account_id}
          </MDTypography>
        </div>

        <div style={{ marginBottom: 16 }}>
          <MDTypography variant="body2" style={{ marginBottom: 8 }}>
            拼好房（APP）登录权限
          </MDTypography>
          <Select
            value={editForm.allow_login}
            onChange={(val) => setEditForm({ ...editForm, allow_login: val })}
            style={{ width: "100%" }}
          >
            <Option value={1}>允许登录</Option>
            <Option value={2}>禁止登录</Option>
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <MDTypography variant="body2" style={{ marginBottom: 8 }}>
            民宿管理员权限
          </MDTypography>
          <Select
            value={editForm.is_minsu_admin}
            onChange={(val) => setEditForm({ ...editForm, is_minsu_admin: val })}
            style={{ width: "100%" }}
          >
            <Option value={1}>普通用户</Option>
            <Option value={2}>民宿管理员</Option>
          </Select>
        </div>

        <div>
          <MDTypography variant="body2" style={{ marginBottom: 8 }}>
            禁用/备注原因
          </MDTypography>
          <TextArea
            rows={3}
            value={editForm.reason}
            onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
            placeholder="禁止登录时请填写原因"
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default UserManager;